'use client';

import { useEffect, useRef } from 'react';
import type { ChatSidebarProps } from './types';
import { SparkleIcon } from '../search/icons';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { useClientConfig } from '@/lib/clients/ClientContext';

export default function ChatSidebar({
  messages,
  isLoading,
  searchProgress,
  onSendMessage,
}: ChatSidebarProps) {
  const clientConfig = useClientConfig();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="w-[380px] flex-shrink-0 flex flex-col h-full border-l border-[#E5E7EB] bg-white">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E5E7EB] flex-shrink-0">
        <SparkleIcon className="w-5 h-4" />
        <h3 className="text-[14px] font-bold font-gotham text-text-primary">
          {clientConfig.ui.strings.aiAssistantTitle}
        </h3>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto overscroll-contain custom-scrollbar p-4 flex flex-col gap-3">
        {messages.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4 gap-3">
            <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center">
              <SparkleIcon className="w-6 h-5" />
            </div>
            <p className="text-[13px] font-gotham text-[#6A7282] leading-[20px]">
              Γεια σας! Πώς μπορώ να σας βοηθήσω;
            </p>
            <p className="text-[12px] font-gotham text-[#99A1AF] leading-[18px]">
              Ρωτήστε με για προϊόντα, δώρα ή οτιδήποτε χρειάζεστε
            </p>
          </div>
        )}

        {messages.map(msg => (
          <ChatMessage key={msg.id} message={msg} />
        ))}

        {/* Search progress indicator */}
        {searchProgress && (
          <div className="flex items-center gap-2 px-3 py-2 bg-brand/10 rounded-lg animate-fadeIn">
            <div className="w-3 h-3 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            <span className="text-[12px] font-gotham text-brand">
              Αναζήτηση: {searchProgress}
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInput onSend={onSendMessage} disabled={isLoading} />
    </div>
  );
}
